import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = still checking, false = anonymous, object = signed in
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      // Re-fetch from /auth/me so any server-side role changes (e.g. the
      // first-admin bootstrap that runs on /me too) are reflected immediately
      // without requiring a manual page refresh.
      try {
        const { data: me } = await api.get("/auth/me");
        setUser(me);
        return me;
      } catch {
        return data.user;
      }
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      try {
        const { data: me } = await api.get("/auth/me");
        setUser(me);
        return me;
      } catch {
        return data.user;
      }
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  const updateProfile = async (patch) => {
    const { data } = await api.patch("/users/me", patch);
    setUser(data);
    return data;
  };

  /** One-tap public demo. Mints an ephemeral demo user server-side and
   * refreshes `user` so protected routes let them in. */
  const startDemoSession = async (role) => {
    try {
      const { data } = await api.post("/auth/demo-session", { role });
      await refresh();
      return data;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const exitDemoSession = async () => {
    try {
      await api.post("/auth/demo-session/exit");
    } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user, error, setError, login, register, logout, refresh,
        refreshUser: refresh, updateProfile,
        startDemoSession, exitDemoSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
